import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { AppConfig } from '@digilize/shared/definitions/src';
import { APP_CONFIG } from '@digilize/shared/utils/tokens/src';
import { TranslocoService } from '@jsverse/transloco';
import { merge, Subscription } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CustomModuleTitleStrategyService extends TitleStrategy {
  // One per navigation: the previous page's subscriptions would otherwise set its title again on a language change.
  private titleSubscription?: Subscription;

  constructor(
    private translocoService: TranslocoService,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private title: Title // @Inject(TRANSLOCO_SCOPE) private translocoScope: any
  ) {
    super();
  }

  updateTitle(snapshot: RouterStateSnapshot): void {
    this.titleSubscription?.unsubscribe();
    const title = this.buildTitle(snapshot);
    if (!title) return;
    const scope = title.split('.')[0];

    // A key Transloco cannot resolve yet comes back as itself: the root file is always there, a lazy module's scope
    // only once that module has rendered its first key. Ask for the key alone: a whole scope cannot be unflattened
    // when a field's key sits next to its `.info` tooltip key, as most scope files have.
    const translated$ = this.translocoService.selectTranslate(title).pipe(filter((translated) => translated !== title));
    const scopeLoaded$ = this.translocoService.events$.pipe(
      filter((ev) => ev.type === 'translationLoadSuccess' && ev.payload.scope === scope),
      switchMap(() => this.translocoService.selectTranslate(title).pipe(first()))
    );

    this.titleSubscription = merge(translated$, scopeLoaded$).subscribe((translated) => {
      const suffix = this.appConfig.appTitle ? ' - ' + this.appConfig.appTitle : '';
      this.title.setTitle(translated + suffix);
    });
  }
}
